import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import * as moment from 'moment';
import * as XLSX from 'xlsx';

export interface ExtractedTripData {
  // Client / Patient Info (priorityClient)
  patientName?: string;
  memberId?: string;
  clientId?: string;
  phone?: string;
  contactNumber?: string;
  preferredPhone?: string;
  cellPhone?: string;
  email?: string;
  dob?: string;
  weight?: string;
  gender?: string;

  // Funding Source / Broker
  fundingSource?: string;
  healthplanCode?: string;
  fundingSourceId?: string;
  fundingSourceObj?: any;

  // Authorization & Identifiers
  authorizationNumber?: string;
  tripId?: string;
  rawId?: string;
  ipa?: string;
  primaryDiagnosis?: string;
  tripReason?: string | string[];
  noOfEscorts?: string | number;

  // Date & Scheduling
  dateOfService?: string;
  pickupTime?: string;
  appointmentTime?: string;
  dropoffTime?: string;
  checkInTime?: string;
  returnPickupTime?: string;

  // Modifiers & Flags
  isRoundTrip?: boolean;
  isWillCall?: boolean;
  isDialysis?: boolean;
  isMethadone?: boolean;
  isStandingOrder?: boolean;
  isRecurring?: boolean;
  isConfirmedTrip?: boolean;
  isVipTrip?: boolean;

  // Origin Address Details (originAddressAdditional)
  pickupAddress?: string;
  pickupLocationName?: string;
  pickupRoom?: string;
  pickupFloor?: string;
  pickupApt?: string;
  pickupPhone?: string;
  pickupModifier?: string;
  pickupModifierTwo?: string;
  zipCodeOrg?: string;
  serviceArea?: string;

  // Destination Address Details (destinationAddressAdditional)
  dropoffAddress?: string;
  dropoffLocationName?: string;
  dropoffRoom?: string;
  dropoffFloor?: string;
  dropoffApt?: string;
  dropoffPhone?: string;
  dropoffModifier?: string;
  dropoffModifierTwo?: string;
  zipCodeDest?: string;
  serviceAreaDest?: string;

  // Mode of Transport & LOS
  modeOfTransport?: string;
  spaceType?: string;
  levelOfService?: string;

  // Gate Code & Notes
  gateCode?: string;
  notes?: string;
  dispatchNote?: string;
  companyNote?: string;
  specialInstructions?: string;

  // Multi-Leg & Return Journey Support
  legs?: ExtractedTripData[];
  legNumber?: number;
  totalLegs?: number;
  isCombinedRoundTrip?: boolean;

  rawSource?: any;
  _rawFile?: File;
}

export type SupportedFileType = 'image' | 'pdf' | 'excel' | 'text' | 'unknown';

@Injectable({
  providedIn: 'root'
})
export class NewTripExtractorService {

  // Dedicated AI Server Configuration (Ubuntu Ollama / Qwen3.5 9B)
  readonly AI_BASE_URL = environment.AI_BASE_URL;
  readonly AI_MODEL = 'qwen3.5:9b-q4_K_M';
  readonly AI_REQUEST_TIMEOUT_MS = 600000; // 10 minutes timeout for large AI document parsing

  constructor() { }

  /**
   * Detect file category based on extension or mime type
   */
  detectFileType(file: File): SupportedFileType {
    const name = file.name.toLowerCase();
    const type = (file.type || '').toLowerCase();

    if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.webp') || type.startsWith('image/')) {
      return 'image';
    }
    if (name.endsWith('.pdf') || type === 'application/pdf') {
      return 'pdf';
    }
    if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv') ||
        type.includes('spreadsheet') || type.includes('excel') || type === 'text/csv') {
      return 'excel';
    }
    if (name.endsWith('.txt') || name.endsWith('.log') || type === 'text/plain') {
      return 'text';
    }
    return 'unknown';
  }

  /**
   * Main entrypoint to extract trip data from any supported document (Image, PDF, Excel, Text) using AI.
   */
  async extractTripFromFile(
    file: File,
    options?: {
      selectedFundingSource?: any;
    }
  ): Promise<ExtractedTripData[]> {
    const fileType = this.detectFileType(file);
    if (fileType === 'unknown') {
      throw new Error(`Unsupported file format (${file.name}). Please upload an Image (.png, .jpg), PDF (.pdf), Excel (.xlsx, .xls, .csv), or Text (.txt) file.`);
    }

    const selectedFundingSource = options ? options.selectedFundingSource : undefined;
    return await this.extractWithLocalAi(file, fileType, selectedFundingSource);
  }

  readonly NEMT_SYSTEM_PROMPT =
    'You are an expert Non-Emergency Medical Transportation (NEMT) AI engine and dispatch document analyzer. ' +
    'Your mission is to extract all trip records from medical transportation documents, broker manifests, trip sheets, portal screenshots, spreadsheets, and scanned PDFs with zero hallucinations and maximum accuracy.\n\n' +
    'Output STRICTLY a valid JSON object matching this schema:\n' +
    '{"trips":[{"patientName":null,"memberId":null,"phone":null,"preferredPhone":null,"dob":null,"weight":null,"gender":null,"fundingSource":null,"healthplanCode":null,"authorizationNumber":null,"tripId":null,"ipa":null,"primaryDiagnosis":null,"tripReason":null,"dateOfService":null,"pickupTime":null,"appointmentTime":null,"checkInTime":null,"returnPickupTime":null,"isRoundTrip":false,"isWillCall":false,"isDialysis":false,"isMethadone":false,"pickupAddress":null,"pickupLocationName":null,"pickupRoom":null,"pickupFloor":null,"pickupApt":null,"pickupPhone":null,"dropoffAddress":null,"dropoffLocationName":null,"dropoffRoom":null,"dropoffFloor":null,"dropoffApt":null,"dropoffPhone":null,"modeOfTransport":null,"gateCode":null,"notes":null,"specialInstructions":null}]}\n\n' +
    'CRITICAL OPERATIONAL RULES:\n' +
    '1. MULTI-LEG, ROUND-TRIP & MANIFEST EXTRACTION:\n' +
    '   - Extract every distinct trip leg, ride, or manifest row as a separate object in the "trips" array.\n' +
    '   - Legs may NOT have letter suffixes (A, B, C). They may have numeric IDs (e.g. 17318573, 17318574), sequential IDs, unrelated IDs, or no IDs. Extract each individual leg independently.\n' +
    '   - Recognize all leg indicators: "Leg 1 / Leg 2", "Leg A / Leg B", "Outbound / Return", "Pick / Drop", "Stop 1 / Stop 2", or repeated passenger records.\n' +
    '   - If a single trip form contains both an initial pickup time AND an explicit return pickup section, set isRoundTrip=true and populate returnPickupTime.\n' +
    '2. ADDRESS VS. LOCATION NAME DISAMBIGUATION (STRICT ISOLATION & MULTI-LINE JOIN):\n' +
    '   - "pickupAddress" / "dropoffAddress": MUST contain ONLY physical street addresses (e.g., "2204 Halltown Rd, Spruce Pine, NC 28777" or "1800 Medical Center Pkwy, STE 200, Murfreesboro, TN 37129").\n' +
    '   - CRITICAL MULTI-LINE ADDRESS RULE: In dispatch documents and manifests, street addresses frequently span multiple lines (e.g., Line 1: "2204 Halltown Rd", Line 2: "Spruce Pine, NC 28777"). You MUST ALWAYS concatenate all lines into ONE complete physical address with street, city, state, and zip code. NEVER extract only the first line.\n' +
    '   - NEVER include dates, times (e.g. "5:33 PM"), phone numbers, or notes inside address fields.\n' +
    '   - "pickupLocationName" / "dropoffLocationName": Extract facility, clinic, hospital, doctor, or business names (e.g. "Tennessee Orthopedic Alliance", "Eisenhower Health", "Mcleod Center"). If residence, use "Residence".\n' +
    '   - NEVER swap physical street addresses and facility names.\n' +
    '   - "pickupRoom" / "dropoffRoom": Extract room, suite, or bed numbers (e.g. "Room 4016B", "STE 200").\n' +
    '3. TIME FIELDS & WILL-CALL RULES:\n' +
    '   - All time fields MUST be in standard 24-hour HH:mm format (e.g., "04:00", "13:48", "17:33", "09:15"). Convert 12-hour AM/PM times accurately (e.g., 1:48 PM -> 13:48, 5:33 PM -> 17:33, 12:15 PM -> 12:15, 12:30 AM -> 00:30).\n' +
    '   - "pickupTime": Initial departure / pickup / ready / load / stage 1 time.\n' +
    '   - "appointmentTime": Arrival / appointment / dropoff / DO / treatment / due / clinic time.\n' +
    '   - "returnPickupTime": Return departure / 2nd leg pickup time.\n' +
    '   - WILL-CALL HANDLING: If a pickup or return is marked "Will Call", "W/C", "WC", "Call In", "Call when ready", "CWR", "Open Return", or "TBD":\n' +
    '     * Set isWillCall: true.\n' +
    '     * Set pickupTime: null (or returnPickupTime: null).\n' +
    '     * If the leg still specifies an appointment / dropoff time (DO Time), extract that DO time into "appointmentTime". DO NOT erase appointment time due to Will Call.\n' +
    '   - DO NOT fabricate missing times. If only Pickup Time exists, leave appointmentTime as null. If only Appt Time exists, leave pickupTime as null.\n' +
    '4. DATES & DEMOGRAPHICS:\n' +
    '   - "dateOfService": The scheduled transport / ride / service date in YYYY-MM-DD format. Do NOT confuse Date of Service with patient Date of Birth (dob).\n' +
    '   - "dob": Patient date of birth in YYYY-MM-DD format.\n' +
    '   - "phone": Standard contact phone number.\n' +
    '   - "memberId": Medicaid ID, Member ID, Subscriber ID, CIN, Policy #, MRN, or Client ID.\n' +
    '5. MODE OF TRANSPORT (STANDARDIZED):\n' +
    '   - Map transport mode strictly to one of: "Wheelchair", "Ambulatory", "Gurney", "Stretcher", "Bariatric".\n' +
    '   - "W/C", "Chair", "Wheelchair", "Manual W/C" -> "Wheelchair".\n' +
    '   - "Ambulatory", "Walk", "Sedan", "Standard", "Curb" -> "Ambulatory".\n' +
    '   - "Gurney", "Stretcher", "Bed", "Litter" -> "Gurney".\n' +
    '6. CLINICAL & GATE CODE FLAGS:\n' +
    '   - "isDialysis": Set to true if destination, diagnosis, or notes mention Dialysis, DaVita, Fresenius, Renal, or Kidney.\n' +
    '   - "isMethadone": Set to true if destination, diagnosis, or notes mention Methadone, Substance, or Behavioral Treatment.\n' +
    '   - "gateCode": Extract ONLY if an explicit access/gate/door/keypad security code is provided (e.g., "#1234", "Gate: 4421"). Return null if absent.\n' +
    '7. ZERO-HALLUCINATION & OUTPUT COMPLIANCE:\n' +
    '   - If any field is not explicitly present or directly inferable from the document, return null.\n' +
    '   - Output ONLY the JSON object. Do not wrap in markdown quotes, explanatory commentary, or conversational prefixes.';

  /**
   * Primary AI Extractor:
   * - Images: Sent directly as Base64 Data URL to Qwen3.5 9B Vision
   * - PDFs: Rendered to page images and sent to Qwen3.5 9B Vision
   * - Excel / CSV: Converted to structured table text and sent to Qwen3.5 9B Text
   * - Text: Sent directly to Qwen3.5 9B Text
   */
  async extractWithLocalAi(
    file: File,
    fileType: SupportedFileType,
    selectedFundingSource?: any
  ): Promise<ExtractedTripData[]> {
    const prompt = this.NEMT_SYSTEM_PROMPT;

    // 1. IMAGE: send directly as Vision Image URL to Qwen3.5 9B (No Tesseract)
    if (fileType === 'image') {
      const dataUrl = await this.readFileAsDataUrl(file);
      return await this.callAiVision([dataUrl], prompt, selectedFundingSource, file);
    }

    // 2. PDF: Fast text extraction first (2-5s), fallback to vision only for scanned images
    if (fileType === 'pdf') {
      const pdfContent = await this.readPdfContent(file);
      if (pdfContent.text && pdfContent.text.trim().length >= 40) {
        return await this.callAiText(pdfContent.text, prompt, selectedFundingSource, file);
      }
      if (pdfContent.images && pdfContent.images.length > 0) {
        return await this.callAiVision(
          pdfContent.images,
          prompt,
          selectedFundingSource,
          file,
          pdfContent.text
        );
      }
      if (pdfContent.text) {
        return await this.callAiText(pdfContent.text, prompt, selectedFundingSource, file);
      }
    }

    // 3. EXCEL / CSV: convert spreadsheet to table text and send to Qwen3.5 9B Text
    if (fileType === 'excel') {
      const excelText = await this.readExcelAsText(file);
      return await this.callAiText(excelText, prompt, selectedFundingSource, file);
    }

    // 4. TEXT: read plain text and send to Qwen3.5 9B Text
    if (fileType === 'text') {
      const text = await this.readFileAsText(file);
      return await this.callAiText(text, prompt, selectedFundingSource, file);
    }

    throw new Error(`Unsupported file type: ${fileType}`);
  }

  /**
   * Universal HTTP POST helper for AI Server:
   * Sends requests directly to AI chat completions API with extended timeout
   */
  private async postToAi(payload: any): Promise<any> {
    const directUrl = `${this.AI_BASE_URL}/chat/completions`;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutId = controller
      ? setTimeout(() => controller.abort(), this.AI_REQUEST_TIMEOUT_MS)
      : null;

    try {
      const res = await fetch(directUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-API-Key': 'nemt_ai_7f3c9a1e4b8d2f60c5e17a94b0d3c86e' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      });
      if (res.ok) {
        return await res.json();
      }
      const errText = await res.text().catch(() => '');
      throw new Error(`AI Server Error (${res.status}): ${res.statusText}${errText ? ' - ' + errText : ''}`);
    } catch (err) {
      if (err && (err as any).name === 'AbortError') {
        throw new Error(`AI Request timed out after ${Math.round(this.AI_REQUEST_TIMEOUT_MS / 60000)} minutes. The AI server took too long to respond.`);
      }
      throw err;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  /**
   * Call AI with Vision input (Images, rendered PDF pages)
   */
  async callAiVision(
    imageUrls: string[],
    systemPrompt: string,
    selectedFundingSource?: any,
    file?: File,
    extraText?: string
  ): Promise<ExtractedTripData[]> {
    const userContent: any[] = [
      {
        type: 'text',
        text: extraText && extraText.trim().length > 10
          ? `Extract all NEMT trip details from this document image/layout. Digital text extracted from document:\n${extraText}`
          : 'Extract all NEMT trip details from this document image/layout into JSON:'
      }
    ];

    for (const url of imageUrls) {
      userContent.push({
        type: 'image_url',
        image_url: {
          url: url
        }
      });
    }

    const payload = {
      model: this.AI_MODEL,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userContent }
      ],
      response_format: { type: 'json_object' },
      reasoning_effort: 'none',
      stream: false,
      temperature: 0.1,
      max_tokens: 4096
    };

    const data = await this.postToAi(payload);
    return this.parseAiJsonResponse(data, selectedFundingSource, file, extraText);
  }

  /**
   * Call AI with Text input (Excel, CSV, plain Text)
   */
  async callAiText(
    text: string,
    systemPrompt: string,
    selectedFundingSource?: any,
    file?: File
  ): Promise<ExtractedTripData[]> {
    const payload = {
      model: this.AI_MODEL,
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: 'Extract all NEMT trip details from this document content into JSON:\n\n' + text }
      ],
      response_format: { type: 'json_object' },
      reasoning_effort: 'none',
      stream: false,
      temperature: 0.1,
      max_tokens: 4096
    };

    const data = await this.postToAi(payload);
    return this.parseAiJsonResponse(data, selectedFundingSource, file, text);
  }

  /**
   * Parse arbitrary JSON or wrapped response from AI Server into ExtractedTripData[]
   */
  private parseAiJsonResponse(data: any, selectedFundingSource?: any, file?: File, rawDocText?: string): ExtractedTripData[] {
    if (!data) {
      throw new Error('Empty response received from AI server.');
    }

    let rawContent: any = data;

    // 1. Extract content from AI Chat Completion choices
    if (data.choices && Array.isArray(data.choices) && data.choices.length > 0) {
      const choice = data.choices[0];
      const msg = choice.message || {};
      if (typeof msg.content === 'string' && msg.content.trim().length > 0) {
        rawContent = msg.content;
      } else if (typeof msg.reasoning === 'string' && msg.reasoning.trim().length > 0) {
        rawContent = msg.reasoning;
      } else if (typeof msg.reasoning_content === 'string' && msg.reasoning_content.trim().length > 0) {
        rawContent = msg.reasoning_content;
      } else if (typeof choice.text === 'string' && choice.text.trim().length > 0) {
        rawContent = choice.text;
      }
    } else if (typeof data.response === 'string') {
      rawContent = data.response;
    }

    // 2. Parse string into JSON
    let parsedResult: any = rawContent;
    if (typeof rawContent === 'string') {
      // Strip any <think>...</think> blocks from reasoning models
      let trimmed = rawContent.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
      const cleanJsonStr = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
      try {
        parsedResult = JSON.parse(cleanJsonStr);
      } catch (e) {
        const jsonMatch = trimmed.match(/\{[\s\S]*\}|\[[\s\S]*\]/);
        if (jsonMatch) {
          try {
            parsedResult = JSON.parse(jsonMatch[0]);
          } catch (e2) {
            parsedResult = { notes: trimmed };
          }
        } else {
          parsedResult = { notes: trimmed };
        }
      }
    }

    // 3. Extract trips array
    let rawTrips: any[] = [];
    if (Array.isArray(parsedResult)) {
      rawTrips = parsedResult;
    } else if (parsedResult && parsedResult.trips && Array.isArray(parsedResult.trips)) {
      rawTrips = parsedResult.trips;
    } else if (parsedResult && parsedResult.data && Array.isArray(parsedResult.data)) {
      rawTrips = parsedResult.data;
    } else if (typeof parsedResult === 'object' && parsedResult !== null) {
      const cleanObj = { ...parsedResult };
      delete cleanObj._meta;
      if (cleanObj.patientName || cleanObj.pickupAddress || cleanObj.dropoffAddress || cleanObj.dateOfService || cleanObj.memberName) {
        rawTrips = [cleanObj];
      }
    }

    const normalizedTrips: ExtractedTripData[] = rawTrips
      .filter((t: any) => t && typeof t === 'object')
      .map((t: any) => this.normalizeTripRecord(t, selectedFundingSource, file, rawDocText));

    if (normalizedTrips.length === 0) {
      throw new Error('No trip records could be identified in the document.');
    }

    return this.groupMultiLegTrips(normalizedTrips);
  }

  /**
   * Intelligently group multi-leg trips / return journeys into combined round-trip records.
   * Identifies when multiple trip records belong to the same passenger on the same day
   * (e.g. Leg A -> Leg B with reverse addresses, or trip IDs with matching root like 8643895-A & 8643895-B).
   */
  /**
   * Intelligently group multiple legs / return trips into unified Round Trip parent objects with attached legs.
   * Handles cases with or without 'A', 'B', 'C' suffixes (numeric IDs, consecutive IDs, different IDs, reverse addresses, same passenger/day).
   */
  public groupMultiLegTrips(trips: ExtractedTripData[]): ExtractedTripData[] {
    if (!trips || trips.length <= 1) return trips;

    const result: ExtractedTripData[] = [];
    const usedIndices: { [key: number]: boolean } = {};

    for (let i = 0; i < trips.length; i++) {
      if (usedIndices[i]) continue;
      const primary: ExtractedTripData = { ...trips[i] };
      const matchingLegs: ExtractedTripData[] = [];

      const pName = (primary.patientName || '').toLowerCase().trim();
      const pId = (primary.memberId || primary.clientId || '').toLowerCase().trim();
      const pPhone = (primary.phone || primary.preferredPhone || '').replace(/\D/g, '');
      const pDos = primary.dateOfService;
      const pBaseTripId = (primary.tripId || primary.rawId || '').replace(/[-_]?[a-zA-Z0-9]$/, '').trim();
      const pRawTripId = (primary.tripId || primary.rawId || '').trim();

      for (let j = i + 1; j < trips.length; j++) {
        if (usedIndices[j]) continue;
        const candidate = trips[j];
        const cName = (candidate.patientName || '').toLowerCase().trim();
        const cId = (candidate.memberId || candidate.clientId || '').toLowerCase().trim();
        const cPhone = (candidate.phone || candidate.preferredPhone || '').replace(/\D/g, '');
        const cDos = candidate.dateOfService;
        const cBaseTripId = (candidate.tripId || candidate.rawId || '').replace(/[-_]?[a-zA-Z0-9]$/, '').trim();
        const cRawTripId = (candidate.tripId || candidate.rawId || '').trim();

        // 1. Check Passenger Identity (Name similarity, Member ID, or Phone match)
        const isSameName = Boolean(pName && cName && (
          pName === cName ||
          pName.split(/\s+/).sort().join(' ') === cName.split(/\s+/).sort().join(' ') ||
          pName.includes(cName) || cName.includes(pName)
        ));
        const isSameId = Boolean(pId && cId && pId === cId);
        const isSamePhone = Boolean(pPhone && cPhone && pPhone.length >= 7 && pPhone === cPhone);
        const isSamePassenger = isSameName || isSameId || isSamePhone;

        // 2. Check Date of Service
        const isSameDate = !pDos || !cDos || pDos === cDos;

        // 3. Check Trip ID Relationships (Suffixes like -A/-B, same base ID, or consecutive numbers)
        const isTripIdRelated = Boolean(
          (pBaseTripId && cBaseTripId && pBaseTripId.length >= 3 && pBaseTripId === cBaseTripId) ||
          (pRawTripId && cRawTripId && pRawTripId === cRawTripId) ||
          (pRawTripId && cRawTripId && /^\d+$/.test(pRawTripId) && /^\d+$/.test(cRawTripId) && Math.abs(parseInt(pRawTripId, 10) - parseInt(cRawTripId, 10)) === 1)
        );

        // 4. Check Reverse Address / Chained Trip (Origin -> Dest reversed or Destination -> Next Pickup)
        const isAddressReverse = Boolean(
          this.isAddressMatch(primary.dropoffAddress, candidate.pickupAddress) ||
          this.isAddressMatch(primary.pickupAddress, candidate.dropoffAddress)
        );

        // 5. Match criteria without requiring letter suffixes (A, B, C):
        // - Same passenger + same date + (reverse address OR related/consecutive ID OR exactly 2 trips in document)
        // - Or related trip ID on same date
        // - Or same passenger + reverse address regardless of date
        const isMatchingLeg = Boolean(
          (isSamePassenger && isSameDate && (isAddressReverse || isTripIdRelated || trips.length === 2)) ||
          (isTripIdRelated && isSameDate) ||
          (isSamePassenger && isAddressReverse)
        );

        if (isMatchingLeg) {
          matchingLegs.push(candidate);
          usedIndices[j] = true;
        }
      }

      if (matchingLegs.length > 0) {
        // Collect all related legs and sort chronologically so Leg 1 is the earliest outbound
        const allRelatedLegs = [primary, ...matchingLegs];
        allRelatedLegs.sort((a, b) => {
          const timeA = a.pickupTime || a.appointmentTime || '99:99';
          const timeB = b.pickupTime || b.appointmentTime || '99:99';
          return timeA.localeCompare(timeB);
        });

        const sortedPrimary = allRelatedLegs[0];
        const attachedLegs = allRelatedLegs.slice(1);

        sortedPrimary.isRoundTrip = true;
        sortedPrimary.legs = attachedLegs;
        sortedPrimary.totalLegs = allRelatedLegs.length;
        sortedPrimary.legNumber = 1;
        sortedPrimary.isCombinedRoundTrip = true;

        const returnLeg = attachedLegs[attachedLegs.length - 1];
        if (returnLeg) {
          sortedPrimary.returnPickupTime = returnLeg.pickupTime || returnLeg.appointmentTime || sortedPrimary.returnPickupTime;
          if (returnLeg.isWillCall || !returnLeg.pickupTime) {
            sortedPrimary.isWillCall = true;
          }
          if (returnLeg.notes && returnLeg.notes.trim() && sortedPrimary.notes !== returnLeg.notes) {
            sortedPrimary.notes = sortedPrimary.notes ? `${sortedPrimary.notes} | Return: ${returnLeg.notes}` : returnLeg.notes;
          }
        }

        usedIndices[i] = true;
        result.push(sortedPrimary);
      } else {
        usedIndices[i] = true;
        result.push(primary);
      }
    }

    return result;
  }

  /**
   * Helper to compare street addresses for route reversal / multi-stop chaining
   */
  private isAddressMatch(addr1?: string, addr2?: string): boolean {
    if (!addr1 || !addr2) return false;
    const clean = (s: string) => s.toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\b(suite|ste|apt|room|rm|unit|bldg|building|floor|fl|dr|drive|st|street|rd|road|ave|avenue|blvd|boulevard|hwy|highway|pkwy|parkway|ct|court|ln|lane|cir|circle|pl|place)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();

    const k1 = clean(addr1);
    const k2 = clean(addr2);
    if (!k1 || !k2) return false;
    if (k1 === k2) return true;

    const words1 = k1.split(' ').filter(w => w.length > 1);
    const words2 = k2.split(' ').filter(w => w.length > 1);
    if (words1.length >= 2 && words2.length >= 2 && words1[0] === words2[0] && words1[1] === words2[1]) {
      return true;
    }
    return k1.includes(k2.substring(0, 12)) || k2.includes(k1.substring(0, 12));
  }

  /**
   * Helper to determine if a string looks like a physical street address
   */
  private isStreetAddress(str: string): boolean {
    if (!str || typeof str !== 'string') return false;
    const s = str.trim();
    if (/^(?:none|null|undefined|n\/a|na|no|unknown|\.)$/i.test(s)) return false;
    // Must contain digits followed by street words, highway, or zip code
    return /\d+\s+[A-Za-z0-9\s.,]+(?:street|st|avenue|ave|road|rd|drive|dr|boulevard|blvd|way|lane|ln|court|ct|circle|cir|highway|hwy|parkway|pkwy|place|pl|suite|ste|apt|box|ca|fl|ny|tx|usa|\d{5})/i.test(s);
  }

  /**
   * Convert extracted object into standardized ExtractedTripData
   */
  private normalizeTripRecord(raw: any, selectedFundingSource?: any, file?: File, rawDocText?: string): ExtractedTripData {
    if (!raw || typeof raw !== 'object') {
      raw = {};
    }

    // Flatten nested objects (e.g. raw.pickup_address.location_name, raw.patient_member.name, etc.)
    const flattenedMap: { [key: string]: any } = {};
    const flatten = (obj: any, prefix = '') => {
      if (!obj || typeof obj !== 'object' || Array.isArray(obj) || obj instanceof Date) return;
      Object.keys(obj).forEach(k => {
        const val = obj[k];
        const dotKey = prefix ? `${prefix}.${k}` : k;
        if (val !== undefined && val !== null) {
          if (typeof val === 'object' && !Array.isArray(val) && !(val instanceof Date)) {
            flatten(val, dotKey);
          } else {
            flattenedMap[dotKey] = val;
            flattenedMap[k] = val;
            // Also store stripped key
            const cleanK = k.toLowerCase().replace(/[^a-z0-9]/g, '');
            flattenedMap[cleanK] = val;
            const cleanDotKey = dotKey.toLowerCase().replace(/[^a-z0-9]/g, '');
            flattenedMap[cleanDotKey] = val;
          }
        }
      });
    };
    flatten(raw);

    // Helper: case-insensitive & key-cleaned lookup on raw object & flattened map
    const getVal = (keys: string[]): string => {
      for (const k of keys) {
        if (raw[k] !== undefined && raw[k] !== null && typeof raw[k] !== 'object' && String(raw[k]).trim() !== '') {
          return String(raw[k]).trim();
        }
        if (flattenedMap[k] !== undefined && flattenedMap[k] !== null && typeof flattenedMap[k] !== 'object' && String(flattenedMap[k]).trim() !== '') {
          return String(flattenedMap[k]).trim();
        }
      }
      for (const k of keys) {
        const cleanK = k.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (flattenedMap[cleanK] !== undefined && flattenedMap[cleanK] !== null && typeof flattenedMap[cleanK] !== 'object' && String(flattenedMap[cleanK]).trim() !== '') {
          return String(flattenedMap[cleanK]).trim();
        }
      }
      return '';
    };

    const cleanAddress = (addr: string): string => {
      if (!addr || typeof addr !== 'string') return '';
      let t = addr.trim();
      if (/^(?:none|null|undefined|n\/a|na|no|unknown|\.)$/i.test(t)) return '';
      // Convert newlines to commas for multi-line addresses
      t = t.replace(/[\r\n]+/g, ', ');
      // Strip out any embedded DO/PU labels, dates, times, phone numbers, or notes from physical street addresses
      t = t.replace(/(?:DO|PU|Pick(?: |-)?up|Drop(?: |-)?off)\s*(?:Date\/Time|Time|Date)[:\s]*/gi, ' ');
      t = t.replace(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\s+\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM)?\b/gi, ' ');
      t = t.replace(/\b\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM)\b/gi, ' ');
      t = t.replace(/(?:Phone\s*#?|Phy|Tel|Mobile|Phone)[:\s]*\+?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/gi, ' ');
      t = t.replace(/\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}/g, ' ');
      t = t.replace(/\b(?:PU|DO|Pick-up|Drop-off)\s*Notes?:.*$/gi, ' ');
      t = t.replace(/\s*,\s*,+/g, ',').replace(/\s+/g, ' ').replace(/^[,\s.-]+|[,\s.-]+$/g, '').trim();
      return t;
    };

    let fundingSource = getVal([
      'payer_details.funding_source', 'payer_details.payer', 'payer_details.broker', 'payer_details.healthplan_code',
      'fundingSource', 'healthplanCode', 'funding_source', 'healthplan_code', 'funding', 'planCode', 'plan_code', 'broker', 'payer', 'insurance', 'program', 'contract', 'account'
    ]);
    if (!fundingSource && rawDocText) {
      const fsMatch = rawDocText.match(/(?:Healthplan\s*Code|Funding\s*Source|Plan\s*Code|Broker|Payer|Contract|Insurance|Program|Account)[:\s]+([A-Za-z0-9_\s-]+)/i);
      if (fsMatch && fsMatch[1]) {
        fundingSource = fsMatch[1].trim();
      }
    }
    if (!fundingSource && selectedFundingSource) {
      fundingSource = selectedFundingSource.displayName || selectedFundingSource.name || selectedFundingSource.code || '';
    }

    let patientName = getVal([
      'patient_member.name', 'patient_member.patient_name', 'patient_member.member_name',
      'patientName', 'patient_name', 'memberName', 'member_name', 'name', 'clientName', 'client_name', 'riderName', 'rider_name', 'passengerName', 'passenger_name', 'recipientName', 'recipient_name', 'customerName', 'customer_name'
    ]);
    if (!patientName && rawDocText) {
      const nameMatch = rawDocText.match(/(?:Patient\s*Name|Member\s*Name|Client\s*Name|Rider\s*Name|Passenger\s*Name|Recipient\s*Name|Name\s*of\s*Patient|Name)[:\s]+([A-Za-z\s.,'-]+)/i);
      if (nameMatch && nameMatch[1]) {
        patientName = nameMatch[1].trim();
      }
    }

    let memberId = getVal([
      'patient_member.member_id', 'patient_member.patient_id', 'patient_member.id',
      'memberId', 'member_id', 'clientId', 'client_id', 'patientId', 'patient_id', 'subscriberId', 'subscriber_id', 'medicaidId', 'medicaid_id', 'cin', 'mrn', 'chartNumber', 'chart_number', 'policyNumber', 'policy_number', 'recipientId', 'recipient_id', 'idNumber', 'id_number'
    ]);
    if (!memberId && rawDocText) {
      const memMatch = rawDocText.match(/(?:Member\s*ID|Member\s*#|Client\s*ID|Client\s*#|Patient\s*ID|Subscriber\s*ID|Subscriber\s*#|Medicaid\s*ID|Medicaid\s*#|CIN|Policy\s*#|Chart\s*#|MRN|ID\s*#)[:\s]+([A-Za-z0-9-]+)/i);
      if (memMatch && memMatch[1]) {
        memberId = memMatch[1].trim();
      }
    }

    let phone = this.cleanPhoneNumber(getVal([
      'patient_member.phone', 'patient_member.phone_number', 'patient_member.cell_phone',
      'phone', 'phoneNumber', 'phone_number', 'homePhone', 'home_phone', 'preferredPhone', 'preferred_phone', 'contactNumber', 'contact_number', 'cellPhone', 'cell_phone', 'telephone', 'tel', 'mobile', 'contactPhone', 'contact_phone'
    ]));
    if (!phone && rawDocText) {
      const phoneMatch = rawDocText.match(/(?:Home\s*Phone|Preferred\s*Phone|Cell\s*Phone|Contact\s*Number|Phone|Telephone|Tel|Mobile)[:\s]+([\(\)0-9\s\-+.]+)/i);
      if (phoneMatch && phoneMatch[1]) {
        phone = this.cleanPhoneNumber(phoneMatch[1]);
      }
    }

    const email = getVal(['patient_member.email', 'email', 'emailAddress', 'email_address']);
    let dob = this.formatDateString(getVal([
      'patient_member.dob', 'patient_member.date_of_birth', 'patient_member.birth_date',
      'dob', 'dateOfBirth', 'date_of_birth', 'patientDob', 'patient_dob', 'birthDate', 'birth_date'
    ]));
    if (!dob && rawDocText) {
      const dobMatch = rawDocText.match(/(?:Patient\s*DOB|DOB|Date\s*of\s*Birth|Birth\s*Date)[:\s\r\n]+([0-9]{1,2}\s*[\/\-.]\s*[0-9]{1,2}\s*[\/\-.]\s*[0-9]{2,4}|[A-Za-z]{3,9}\s+[0-9]{1,2},?\s+[0-9]{4}|[0-9]{4}[\/\-.][0-9]{1,2}[\/\-.][0-9]{1,2})/i);
      if (dobMatch && dobMatch[1]) {
        dob = this.formatDateString(dobMatch[1]);
      }
    }

    let dos = this.formatDateString(getVal([
      'schedule_times.dos', 'schedule_times.date', 'schedule_times.service_date', 'schedule_times.trip_date',
      'dateOfService', 'date_of_service', 'dos', 'tripDate', 'trip_date',
      'serviceDate', 'service_date', 'rideDate', 'ride_date', 'apptDate', 'appt_date',
      'appointmentDate', 'appointment_date', 'scheduledDate', 'scheduled_date',
      'transportDate', 'transport_date', 'dateOfTransport', 'date_of_transport',
      'service_dos', 'serviceDos', 'date', 'trip_dos'
    ]));

    if (!dos && rawDocText) {
      // 1. Check for specific Date of Service / DOS labels (handling multiline or OCR spaces)
      const dosMatch = rawDocText.match(/(?:Date\s*of\s*Service|Date\s*Of\s*Service|DOS|Service\s*Date|Trip\s*Date|Ride\s*Date|Appt\s*Date|Appointment\s*Date|Scheduled\s*Date|Transport\s*Date|Date\s*of\s*Transport|Date)[:\s\r\n]+(?:[A-Za-z]+,\s*)?([0-9]{1,2}\s*[\/\-.]\s*[0-9]{1,2}\s*[\/\-.]\s*[0-9]{2,4}|[A-Za-z]{3,9}\s+[0-9]{1,2},?\s+[0-9]{4}|[0-9]{4}[\/\-.][0-9]{1,2}[\/\-.][0-9]{1,2})/i);
      if (dosMatch && dosMatch[1]) {
        dos = this.formatDateString(dosMatch[1]);
      }
    }

    // 2. Global Date Scanner fallback:
    // If DOS is still not found, scan all dates in rawDocText. Any date that is NOT dob and represents a modern year is DOS!
    if (!dos && rawDocText) {
      const allDateMatches = rawDocText.match(/\b(?:\d{1,2}\s*[\/\-.]\s*\d{1,2}\s*[\/\-.]\s*\d{2,4}|\d{4}[\/\-.]\d{1,2}[\/\-.]\d{1,2}|[A-Za-z]{3,9}\s+\d{1,2},?\s+\d{4})\b/g) || [];
      for (const dCandidate of allDateMatches) {
        const formatted = this.formatDateString(dCandidate);
        if (formatted && formatted !== dob) {
          const yr = moment(formatted).year();
          if (yr >= 2020 && yr <= 2099) {
            dos = formatted;
            break;
          }
        }
      }
    }

    let rawWeight = getVal(['weight', 'patientWeight', 'patient_weight', 'heightAndWeight', 'height_and_weight', 'patientHeightAndWeight']);
    if (!rawWeight && rawDocText) {
      const weightMatch = rawDocText.match(/(?:height\s*and\s*weight|patient\s*weight|weight)[:\s]+[^\d]*(\d{2,3})\s*(?:lbs|lb|pounds|kg)?/i);
      if (weightMatch && weightMatch[1]) {
        rawWeight = weightMatch[1];
      }
    }
    if (rawWeight) {
      const lbsMatch = rawWeight.match(/(\d{2,3})\s*(?:lbs|lb|pounds|kg)?/i);
      if (lbsMatch) {
        rawWeight = lbsMatch[1];
      } else {
        rawWeight = rawWeight.replace(/[^0-9.]/g, '');
      }
    }

    // Gate Code, Access Code, Door Code extraction
    const isInvalidGateCode = (code: string): boolean => {
      if (!code || typeof code !== 'string') return true;
      const c = code.trim().toLowerCase();
      return /^(?:none|null|undefined|n\/a|na|no|unknown|\.|-|drop|pickup|dropoff|address|location|room|gate|pick\s*up|drop\s*off)$/i.test(c);
    };

    let gateCode = getVal(['gateCode', 'gate_code', 'gate', 'accessCode', 'access_code', 'doorCode', 'door_code', 'entryCode', 'entry_code', 'buzzer', 'lockbox', 'keyCode', 'key_code', 'securityCode', 'security_code']);
    if (isInvalidGateCode(gateCode)) {
      gateCode = '';
    }

    if (!gateCode && rawDocText) {
      const gateMatch = rawDocText.match(/(?:GATE\s*CODE|Gate\s*Code|Gate\s*#|Access\s*Code|Door\s*Code|Entry\s*Code|Buzzer|Key\s*Code|Lock\s*Box|Security\s*Code|Building\s*Code)[:\s]+([A-Za-z0-9#-]+)/i);
      if (gateMatch && gateMatch[1] && !isInvalidGateCode(gateMatch[1])) {
        gateCode = gateMatch[1].trim();
      }
    }

    // 1. Pickup & Dropoff Address Resolution
    let pickupAddress = cleanAddress(getVal([
      'pickup_address.address', 'pickup_address.street', 'pickup_address.addr', 'pickup_address.location_address',
      'pickupAddress', 'pickup_address', 'pick_up_address', 'pickUpAddress',
      'originAddress', 'origin_address', 'origin_addr', 'origin', 'puAddress', 'pu_address', 'pu_addr',
      'pickUp', 'pickup', 'pick_up', 'fromAddress', 'from_address', 'from_addr', 'from',
      'residenceAddress', 'residence_address', 'homeAddress', 'home_address', 'clientAddress', 'client_address',
      'boardingLocation', 'boarding_location', 'boardingAddress', 'boarding_address',
      'startLocation', 'start_location', 'startPoint', 'start_point',
      'legAOrigin', 'leg1Origin', 'senderAddress', 'sender_address',
      'pickup_street', 'pickup_location_address', 'pickup_addr'
    ]));

    let dropoffAddress = cleanAddress(getVal([
      'dropoff_address.address', 'dropoff_address.street', 'dropoff_address.addr', 'dropoff_address.location_address',
      'dropoffAddress', 'dropoff_address', 'drop_off_address', 'dropOffAddress',
      'destinationAddress', 'destination_address', 'destination_addr', 'destination', 'doAddress', 'do_address', 'do_addr',
      'dropOff', 'dropoff', 'drop_off', 'toAddress', 'to_address', 'to_addr', 'to',
      'facilityAddress', 'facility_address', 'clinicAddress', 'clinic_address', 'hospitalAddress', 'hospital_address',
      'alightingLocation', 'alighting_location', 'alightingAddress', 'alighting_address',
      'endLocation', 'end_location', 'endPoint', 'end_point', 'deliveryAddress', 'delivery_address',
      'legADestination', 'leg1Destination', 'receiverAddress', 'receiver_address',
      'dropoff_street', 'dropoff_location_address', 'dropoff_addr'
    ]));

    let pickupLocationName = cleanAddress(getVal([
      'pickup_address.location_name', 'pickup_address.location', 'pickup_address.name', 'pickup_address.facility_name',
      'pickupLocationName', 'pickup_location_name', 'pick_up_location_name', 'pickUpLocationName',
      'pickupLocation', 'pickup_location', 'pick_up_location', 'pickUpLocation',
      'originLocationName', 'origin_location_name', 'originLocation', 'origin_location',
      'pickupFacility', 'pickup_facility', 'originFacility', 'origin_facility',
      'pickupDoctor', 'pickup_doctor', 'pickupDrName', 'pickupDr', 'pickupDoctorName',
      'pickupSiteName', 'pickup_site_name', 'pickupSite', 'siteA', 'site_a'
    ]));

    let dropoffLocationName = cleanAddress(getVal([
      'dropoff_address.location_name', 'dropoff_address.location', 'dropoff_address.name', 'dropoff_address.facility_name',
      'dropoffLocationName', 'dropoff_location_name', 'drop_off_location_name', 'dropOffLocationName',
      'dropoffLocation', 'dropoff_location', 'drop_off_location', 'dropOffLocation',
      'destinationLocationName', 'destination_location_name', 'destinationLocation', 'destination_location',
      'dropoffFacility', 'dropoff_facility', 'destinationFacility', 'destination_facility',
      'dropoffDoctor', 'dropoff_doctor', 'dropoffDrName', 'dropoffDr', 'dropoffDoctorName',
      'dropoffSiteName', 'dropoff_site_name', 'dropoffSite', 'siteB', 'site_b',
      'clinicName', 'clinic_name', 'facilityName', 'facility_name', 'hospitalName', 'hospital_name'
    ]));

    let pickupRoom = getVal(['pickupRoom', 'pickup_room', 'room', 'rm', 'pickupRm', 'pickup_rm']);
    let pickupFloor = getVal(['pickupFloor', 'pickup_floor', 'floor', 'fl']);
    let dropoffRoom = getVal(['dropoffRoom', 'dropoff_room', 'dropoffRm', 'dropoff_rm']);
    let dropoffFloor = getVal(['dropoffFloor', 'dropoff_floor', 'dropoffFl', 'dropoff_fl']);

    // Smart Room / Floor extraction from Location Names (e.g. "EMC ROOM 4016B" -> location: "EMC", room: "4016B")
    if (pickupLocationName && !pickupRoom) {
      const roomMatch = pickupLocationName.match(/\b(?:ROOM|RM|BED)\s*#?([A-Za-z0-9-]+)/i);
      if (roomMatch && roomMatch[1]) {
        pickupRoom = roomMatch[1].trim();
        pickupLocationName = pickupLocationName.replace(/\b(?:ROOM|RM|BED)\s*#?[A-Za-z0-9-]+/i, '').replace(/^[,\s.-]+|[,\s.-]+$/g, '').trim();
      }
    }
    if (pickupLocationName && !pickupFloor) {
      const floorMatch = pickupLocationName.match(/\b(?:FLOOR|FL)\s*#?([A-Za-z0-9-]+)/i);
      if (floorMatch && floorMatch[1]) {
        pickupFloor = floorMatch[1].trim();
        pickupLocationName = pickupLocationName.replace(/\b(?:FLOOR|FL)\s*#?[A-Za-z0-9-]+/i, '').replace(/^[,\s.-]+|[,\s.-]+$/g, '').trim();
      }
    }

    if (dropoffLocationName && !dropoffRoom) {
      const roomMatch = dropoffLocationName.match(/\b(?:ROOM|RM|BED)\s*#?([A-Za-z0-9-]+)/i);
      if (roomMatch && roomMatch[1]) {
        dropoffRoom = roomMatch[1].trim();
        dropoffLocationName = dropoffLocationName.replace(/\b(?:ROOM|RM|BED)\s*#?[A-Za-z0-9-]+/i, '').replace(/^[,\s.-]+|[,\s.-]+$/g, '').trim();
      }
    }
    if (dropoffLocationName && !dropoffFloor) {
      const floorMatch = dropoffLocationName.match(/\b(?:FLOOR|FL)\s*#?([A-Za-z0-9-]+)/i);
      if (floorMatch && floorMatch[1]) {
        dropoffFloor = floorMatch[1].trim();
        dropoffLocationName = dropoffLocationName.replace(/\b(?:FLOOR|FL)\s*#?[A-Za-z0-9-]+/i, '').replace(/^[,\s.-]+|[,\s.-]+$/g, '').trim();
      }
    }

    // Smart Address / Location Swap if model placed address into location field
    if (!pickupAddress && this.isStreetAddress(pickupLocationName)) {
      pickupAddress = pickupLocationName;
      pickupLocationName = '';
    } else if (pickupAddress && pickupLocationName && !this.isStreetAddress(pickupAddress) && this.isStreetAddress(pickupLocationName)) {
      const temp = pickupAddress;
      pickupAddress = pickupLocationName;
      pickupLocationName = temp;
    }

    if (!dropoffAddress && this.isStreetAddress(dropoffLocationName)) {
      dropoffAddress = dropoffLocationName;
      dropoffLocationName = '';
    } else if (dropoffAddress && dropoffLocationName && !this.isStreetAddress(dropoffAddress) && this.isStreetAddress(dropoffLocationName)) {
      const temp = dropoffAddress;
      dropoffAddress = dropoffLocationName;
      dropoffLocationName = temp;
    }

    // Fallback: If pickupAddress is still missing, scan raw document text across multiple label variants
    if (!pickupAddress && rawDocText) {
      const puMatch = rawDocText.match(/(?:Pick(?: |-)?up\s*(?:address|street|addr)|Origin\s*Address|Origin|From\s*Address|From|PU\s*Address|PU|Residence|Home\s*Address|Boarding\s*Location|Starting\s*Location|Start\s*Point|Departure)[:\s]+([^\n\r]+(?:\r?\n[ \t]*[A-Za-z\s.,#-]+(?:,\s*[A-Z]{2}\s*\d{5}|\b\d{5}\b|[A-Za-z]+,\s*[A-Z]{2}))?)/i);
      if (puMatch && puMatch[1]) {
        const candidate = cleanAddress(puMatch[1]);
        if (candidate) {
          pickupAddress = candidate;
        }
      }

      // If still missing, scan all street addresses in the document text (including multiline)
      if (!pickupAddress) {
        const addressRegex = /\b\d{1,6}\s+[A-Za-z0-9\s.,#-]+(?:Dr|Drive|Rd|Road|St|Street|Ave|Avenue|Blvd|Boulevard|Hwy|Highway|Way|Lane|Ln|Court|Ct|Circle|Cir|Pkwy|Parkway|Place|Pl|Apt|Suite|Ste)\b(?:[^\n\r]*(?:\r?\n[ \t]*[A-Za-z\s.,#-]+,\s*[A-Z]{2}\s*\d{5})?)?/gi;
        const matches = rawDocText.match(addressRegex) || [];
        for (const m of matches) {
          const cleaned = cleanAddress(m);
          if (cleaned && cleaned !== dropoffAddress && this.isStreetAddress(cleaned)) {
            pickupAddress = cleaned;
            break;
          }
        }
      }
    }

    // Fallback for dropoffAddress from raw document text across multiple label variants
    if (!dropoffAddress && rawDocText) {
      const doMatch = rawDocText.match(/(?:Drop(?: |-)?off\s*(?:address|street|addr)|Destination\s*Address|Destination|To\s*Address|To|DO\s*Address|DO|Facility\s*Address|Clinic\s*Address|Hospital\s*Address|Alighting\s*Location|Delivery\s*Address|End\s*Location|End\s*Point|Arrival)[:\s]+([^\n\r]+(?:\r?\n[ \t]*[A-Za-z\s.,#-]+(?:,\s*[A-Z]{2}\s*\d{5}|\b\d{5}\b|[A-Za-z]+,\s*[A-Z]{2}))?)/i);
      if (doMatch && doMatch[1]) {
        const candidate = cleanAddress(doMatch[1]);
        if (candidate) {
          dropoffAddress = candidate;
        }
      }
    }

    if (!pickupLocationName && rawDocText) {
      const puLocMatch = rawDocText.match(/(?:Pick(?: |-)?up\s*Location(?:\/Dr\.?\s*name)?|Origin\s*Location|Origin\s*Facility|Pickup\s*Facility|PU\s*Location|Pickup\s*Doctor|Pick-up\s*Location)[:\s]+([^\n\r]+)/i);
      if (puLocMatch && puLocMatch[1]) {
        pickupLocationName = cleanAddress(puLocMatch[1]);
      }
    }

    if (!dropoffLocationName && rawDocText) {
      const doLocMatch = rawDocText.match(/(?:Drop(?: |-)?off\s*Location(?:\/Dr\.?\s*name)?|Destination\s*Location|Destination\s*Facility|Dropoff\s*Facility|DO\s*Location|Dropoff\s*Doctor|Drop-off\s*Location|Clinic\s*Name|Hospital\s*Name|Facility\s*Name)[:\s]+([^\n\r]+)/i);
      if (doLocMatch && doLocMatch[1]) {
        dropoffLocationName = cleanAddress(doLocMatch[1]);
      }
    }

    // 2. Times & Scheduling (Comprehensive synonym matching across all broker formats)
    let checkInTime = this.formatTimeString(getVal([
      'schedule_times.check_in_time', 'schedule_times.checkin_time', 'schedule_times.ready_time',
      'checkInTime', 'check_in_time', 'checkinTime', 'checkin_time', 'readyTime', 'ready_time', 'registrationTime', 'registration_time'
    ]));
    let appointmentTime = this.formatTimeString(getVal([
      'schedule_times.appt_time', 'schedule_times.appointment_time', 'schedule_times.appt_checkin_time', 'schedule_times.dropoff_time', 'schedule_times.facility_time',
      'appointmentTime', 'appointment_time', 'apptTime', 'appt_time', 'dropoffTime', 'dropoff_time', 'facilityTime', 'facility_time',
      'scheduledDo', 'scheduled_do', 'arrivalTime', 'arrival_time', 'treatmentTime', 'treatment_time', 'endTime', 'end_time',
      'deliveryTime', 'delivery_time', 'clinicTime', 'clinic_time', 'dueTime', 'due_time', 'sessionTime', 'session_time',
      'doTime', 'do_time', 'do', 'destinationTime', 'destination_time', 'destArrivalTime', 'dest_arrival_time'
    ]));
    let pickupTime = this.formatTimeString(getVal([
      'schedule_times.pickup_time', 'schedule_times.pu_time', 'schedule_times.scheduled_pu',
      'pickupTime', 'pickup_time', 'pick_up_time', 'puTime', 'pu_time', 'scheduledPu', 'scheduled_pu', 'callTime', 'call_time',
      'departureTime', 'departure_time', 'departure', 'departTime', 'depart_time', 'startTime', 'start_time',
      'loadTime', 'load_time', 'originTime', 'origin_time', 'stage1Time', 'stage1_time', 'pu', 'estPickup', 'est_pickup'
    ]));
    let returnPickupTime = this.formatTimeString(getVal([
      'schedule_times.return_pickup_time', 'schedule_times.return_time', 'schedule_times.rt_time',
      'returnPickupTime', 'return_pickup_time', 'returnTime', 'return_time', 'rtTime', 'rt_time', 'returnPu', 'return_pu',
      'leg2Pu', 'leg2_pu', 'legBTime', 'leg_b_time', 'returnDeparture', 'return_departure', 'returnPickup', 'return_pickup',
      'pu2', 'pu_2', 'pickupReturn', 'pickup_return', 'secondLegPu', 'second_leg_pu'
    ]));

    if (!checkInTime && rawDocText) {
      const checkInMatch = rawDocText.match(/(?:Check-?in\s*Time|Ready\s*Time|Registration\s*Time)[:\s]+([0-9]{1,2}:[0-9]{2}(?::[0-9]{2})?\s*(?:AM|PM)?)/i);
      if (checkInMatch && checkInMatch[1]) {
        checkInTime = this.formatTimeString(checkInMatch[1]);
      }
    }

    if (!appointmentTime && rawDocText) {
      const apptMatch = rawDocText.match(/(?:Appt\s*time|Appointment\s*Time|Facility\s*Time|DO\s*Time|Drop-?off\s*Time|Treatment\s*Time|Arrival\s*Time|Clinic\s*Time)[:\s]+([0-9]{1,2}:[0-9]{2}(?::[0-9]{2})?\s*(?:AM|PM)?)/i);
      if (apptMatch && apptMatch[1]) {
        appointmentTime = this.formatTimeString(apptMatch[1]);
      }
    }

    if (!pickupTime && rawDocText) {
      const puMatch = rawDocText.match(/(?:Pick-?up\s*Time|PU\s*Time|Departure\s*Time|Ready\s*Time|Start\s*Time|Load\s*Time)[:\s]+([0-9]{1,2}:[0-9]{2}(?::[0-9]{2})?\s*(?:AM|PM)?)/i);
      if (puMatch && puMatch[1]) {
        pickupTime = this.formatTimeString(puMatch[1]);
      }
    }

    if (!returnPickupTime && rawDocText) {
      const retMatch = rawDocText.match(/(?:Return\s*Pick-?up|Return\s*Time|Return\s*PU|Leg\s*2\s*PU|Leg\s*B\s*Time|2nd\s*Leg\s*PU|PU\s*2)[:\s]+([0-9]{1,2}:[0-9]{2}(?::[0-9]{2})?\s*(?:AM|PM)?)/i);
      if (retMatch && retMatch[1]) {
        returnPickupTime = this.formatTimeString(retMatch[1]);
      }
    }

    // 3. Direction & Round Trip
    const rawRoundTrip = raw.isRoundTrip !== undefined ? raw.isRoundTrip : (raw.roundTrip !== undefined ? raw.roundTrip : raw.is_round_trip);
    let isRoundTrip = false;
    if (rawRoundTrip === true || rawRoundTrip === 'true') {
      isRoundTrip = true;
    } else if (returnPickupTime && String(returnPickupTime).trim() !== '') {
      isRoundTrip = true;
    } else {
      const typeStr = String(getVal(['tripType', 'trip_type', 'direction', 'serviceType', 'service_type', 'roundTrip', 'round_trip'])).trim();
      if (/round\s*trip|\bround\b|two[- ]?way|2[- ]?way|both[- ]?ways|return|\bRT\b/i.test(typeStr)) {
        isRoundTrip = true;
      } else if (/one[- ]?way|1[- ]?way|single\s*way|single[- ]?trip|\bOW\b/i.test(typeStr)) {
        isRoundTrip = false;
      } else if (rawDocText && /Round\s*trip/i.test(rawDocText)) {
        isRoundTrip = true;
      } else {
        isRoundTrip = Boolean(rawRoundTrip);
      }
    }

    const isWillCall = Boolean(raw.isWillCall || raw.willCall || raw.is_will_call) ||
      /will\s*call/i.test(String(pickupTime || appointmentTime || returnPickupTime || ''));

    // 4. Mode of Transport Mapping
    let mode = getVal(['modeOfTransport', 'mode_of_transport', 'mode', 'spaceType', 'space_type', 'levelOfService', 'level_of_service', 'transportMode', 'transport_mode', 'mobility']);
    if (!mode && rawDocText) {
      const modeMatch = rawDocText.match(/(?:Mode\s*of\s*transport|Transport\s*mode|LOS|Level\s*of\s*service|Space\s*Type|Mobility)[:\s]+([^\n\r]+)/i);
      if (modeMatch && modeMatch[1]) {
        mode = modeMatch[1].trim();
      }
    }
    if (/\b(?:w\/c|wc|wheel\s*chair|needs\s*w\/c|manual\s*w\/c|electric\s*w\/c)\b/i.test(mode)) {
      mode = 'Wheelchair';
    } else if (/\b(?:ambulatory|walk|sedan|standard|van|taxi|curb)\b/i.test(mode)) {
      mode = 'Ambulatory';
    } else if (/\b(?:gurney|stretcher|bed|litter)\b/i.test(mode)) {
      mode = 'Gurney';
    } else if (/\b(?:bariatric|bari)\b/i.test(mode)) {
      mode = 'Bariatric';
    }

    const isDialysis = Boolean(raw.isDialysis || raw.is_dialysis) || /dialysis|davita|fresenius|renal/i.test(
      (raw.primaryDiagnosis || '') + ' ' + (raw.tripReason || '') + ' ' + dropoffLocationName + ' ' + (raw.notes || '')
    );
    const isMethadone = Boolean(raw.isMethadone || raw.is_methadone) || /methadone/i.test(
      (raw.primaryDiagnosis || '') + ' ' + (raw.tripReason || '') + ' ' + dropoffLocationName + ' ' + (raw.notes || '')
    );

    let notes = (getVal(['notes', 'specialInstructions', 'special_instructions', 'dispatchNote', 'dispatch_note', 'companyNote', 'company_note', 'instructions', 'driverNotes', 'driver_notes'])).trim();
    if (gateCode && !notes.toLowerCase().includes(gateCode.toLowerCase())) {
      notes = notes ? `Gate Code: ${gateCode}. ${notes}` : `Gate Code: ${gateCode}`;
    }

    return {
      patientName: patientName,
      memberId: memberId,
      clientId: memberId,
      phone: phone,
      contactNumber: phone,
      preferredPhone: phone,
      cellPhone: phone,
      email: email,
      dob: dob,
      weight: rawWeight,
      gender: (getVal(['gender', 'sex'])).toLowerCase().trim(),
      fundingSource: fundingSource,
      healthplanCode: fundingSource,
      fundingSourceId: selectedFundingSource ? selectedFundingSource._id : undefined,
      fundingSourceObj: selectedFundingSource,
      authorizationNumber: getVal(['authorizationNumber', 'authorization_number', 'authNumber', 'auth_number', 'auth', 'priorAuth', 'prior_auth', 'paNumber', 'pa_number', 'voucherNumber', 'voucher_number', 'confirmationNumber', 'confirmation_number']),
      tripId: getVal(['tripId', 'trip_id', 'tripNumber', 'trip_number', 'jobId', 'job_id', 'orderNumber', 'order_number', 'rideId', 'ride_id', 'trackingNumber', 'tracking_number']),
      rawId: getVal(['tripId', 'trip_id', 'jobId', 'job_id']),
      ipa: getVal(['ipa', 'medicalGroup', 'medical_group']),
      primaryDiagnosis: getVal(['primaryDiagnosis', 'primary_diagnosis', 'diagnosis', 'dx']),
      tripReason: getVal(['tripReason', 'trip_reason', 'reason', 'purpose']),
      noOfEscorts: getVal(['noOfEscorts', 'no_of_escorts', 'escorts', 'escortCount']),
      dateOfService: dos,
      pickupTime: pickupTime,
      appointmentTime: appointmentTime,
      checkInTime: checkInTime,
      returnPickupTime: returnPickupTime,
      pickupAddress: pickupAddress,
      pickupLocationName: pickupLocationName,
      pickupRoom: pickupRoom,
      pickupFloor: pickupFloor,
      pickupApt: getVal(['pickupApt', 'pickup_apt', 'pickupSuite', 'pickup_suite', 'apt', 'unit']),
      pickupPhone: this.cleanPhoneNumber(getVal(['pickupPhone', 'pickup_phone'])),
      pickupModifier: getVal(['pickupModifier', 'pickup_modifier']),
      dropoffAddress: dropoffAddress,
      dropoffLocationName: dropoffLocationName,
      dropoffRoom: dropoffRoom,
      dropoffFloor: dropoffFloor,
      dropoffApt: getVal(['dropoffApt', 'dropoff_apt', 'dropoffSuite', 'dropoff_suite']),
      dropoffPhone: this.cleanPhoneNumber(getVal(['dropoffPhone', 'dropoff_phone'])),
      dropoffModifier: getVal(['dropoffModifier', 'dropoff_modifier']),
      modeOfTransport: mode,
      spaceType: mode,
      isRoundTrip: isRoundTrip,
      isWillCall: isWillCall,
      isDialysis: isDialysis,
      isMethadone: isMethadone,
      isStandingOrder: Boolean(raw.isStandingOrder || raw.is_standing_order),
      isRecurring: Boolean(raw.isStandingOrder || raw.is_standing_order),
      gateCode: gateCode || undefined,
      notes: notes,
      dispatchNote: notes,
      companyNote: notes,
      specialInstructions: notes,
      rawSource: raw,
      _rawFile: file
    };
  }

  /**
   * Helper: Read file as Base64 Data URL with automatic resolution optimization for Vision models
   */
  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const rawDataUrl = reader.result as string;
        if (typeof Image !== 'undefined' && typeof document !== 'undefined') {
          const img = new Image();
          img.onload = () => {
            const maxDim = 1024;
            let width = img.width;
            let height = img.height;
            if (width > maxDim || height > maxDim) {
              if (width > height) {
                height = Math.round((height * maxDim) / width);
                width = maxDim;
              } else {
                width = Math.round((width * maxDim) / height);
                height = maxDim;
              }
              const canvas = document.createElement('canvas');
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/jpeg', 0.80));
                return;
              }
            }
            resolve(rawDataUrl);
          };
          img.onerror = () => resolve(rawDataUrl);
          img.src = rawDataUrl;
        } else {
          resolve(rawDataUrl);
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Helper: Read file as text
   */
  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  }

  /**
   * Helper: Read file as ArrayBuffer
   */
  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Helper: Convert Excel spreadsheet to formatted CSV / Markdown representation
   */
  private async readExcelAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array', cellDates: true, cellNF: true, cellText: true });
          const textSheets: string[] = [];

          workbook.SheetNames.forEach(sheetName => {
            const sheet = workbook.Sheets[sheetName];
            const csv = XLSX.utils.sheet_to_csv(sheet);
            if (csv && csv.trim()) {
              textSheets.push('--- SHEET: ' + sheetName + ' ---\n' + csv);
            }
          });

          resolve(textSheets.join('\n\n'));
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Helper: Dynamically load PDF.js library if not already present with polyfills
   */
  private async loadPdfJs(): Promise<any> {
    const win = window as any;

    // Polyfill Promise.allSettled for compatibility with older browser/Angular runtimes
    if (!(Promise as any).allSettled) {
      (Promise as any).allSettled = function (promises: Promise<any>[]) {
        return Promise.all(
          (promises || []).map(p => Promise.resolve(p).then(
            value => ({ status: 'fulfilled', value }),
            reason => ({ status: 'rejected', reason })
          ))
        );
      };
    }

    if (win.pdfjsLib) {
      return win.pdfjsLib;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.min.js';
      script.onload = () => {
        const pdfjs = win.pdfjsLib;
        if (pdfjs) {
          pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js';
          resolve(pdfjs);
        } else {
          reject(new Error('pdfjsLib not available after script load'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load PDF.js script from CDN'));
      document.head.appendChild(script);
    });
  }

  /**
   * Helper: Read PDF Content (Extracting text streams and rendering pages to high-res images)
   */
  private async readPdfContent(file: File): Promise<{ text: string; images: string[] }> {
    try {
      const pdfjs = await this.loadPdfJs();
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      const totalPages = pdf.numPages;
      const maxVisionPages = Math.min(totalPages, 2);

      let fullText = '';
      const pageImages: string[] = [];

      for (let i = 1; i <= totalPages; i++) {
        const page = await pdf.getPage(i);
        
        // Extract text across all pages
        try {
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map((item: any) => item.str).join(' ');
          fullText += '--- Page ' + i + ' ---\n' + pageText + '\n\n';
        } catch (tErr) {
          console.warn('Text extraction failed on page ' + i, tErr);
        }

        // Render page screenshot for Vision (first 2 pages to keep CPU latency fast)
        if (i <= maxVisionPages) {
          try {
            const viewport = page.getViewport({ scale: 1.0 });
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            if (ctx) {
              await page.render({ canvasContext: ctx, viewport: viewport }).promise;
              pageImages.push(canvas.toDataURL('image/jpeg', 0.80));
            }
          } catch (renderErr) {
            console.warn('Page canvas rendering failed on page ' + i, renderErr);
          }
        }
      }

      return { text: fullText.trim(), images: pageImages };
    } catch (pdfErr) {
      console.warn('PDF.js parsing error, falling back to raw buffer:', pdfErr);
      const rawText = await this.readRawPdfFallback(file);
      return { text: rawText, images: [] };
    }
  }

  private async readRawPdfFallback(file: File): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const rawBuffer = e.target.result as ArrayBuffer;
          const bytes = new Uint8Array(rawBuffer);
          let rawString = '';
          for (let i = 0; i < bytes.length; i++) {
            rawString += String.fromCharCode(bytes[i]);
          }

          const matches = rawString.match(/\((.*?)\)\s*Tj/g) || [];
          const extractedTokens = matches.map(m => m.replace(/^\(|\)\s*Tj$/g, '').trim()).filter(t => t.length > 0);

          if (extractedTokens.length > 5) {
            resolve(extractedTokens.join(' '));
          } else {
            const cleanText = rawString.replace(/[^\x20-\x7E\r\n]/g, ' ').replace(/\s+/g, ' ').trim();
            resolve(cleanText.substring(0, 8000));
          }
        } catch (err) {
          resolve('');
        }
      };
      reader.onerror = () => resolve('');
      reader.readAsArrayBuffer(file);
    });
  }



  /**
   * Helper: Normalize phone strings to clean digits.
   */
  cleanPhoneNumber(val: any): string {
    if (!val) return '';
    const digits = String(val).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return digits.substring(1);
    }
    return digits.length >= 10 ? digits.slice(-10) : digits;
  }

  /**
   * Helper: Format any date representation to YYYY-MM-DD.
   */
  formatDateString(val: any): string {
    if (!val) return '';
    if (val instanceof Date) {
      return moment(val).format('YYYY-MM-DD');
    }
    let str = String(val).trim();
    if (/^(?:none|null|undefined|n\/a|na|no|unknown|\.|-)$/i.test(str)) {
      return '';
    }

    // Clean OCR artifacts like spaces around slashes/dashes/dots (e.g. "09 / 11 / 2026" -> "09/11/2026")
    str = str.replace(/(\d{1,4})\s*([/.-])\s*(\d{1,2})\s*([/.-])\s*(\d{2,4})/, '$1$2$3$4$5');

    // Extract pure date if accompanied by day of week, timestamps, or text (e.g. "Friday, 09/11/2026" or "09/11/2026 08:30 AM")
    const embeddedNumeric = str.match(/\b(\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{2,4}|\d{4}[\/\-.]\d{1,2}[\/\-.]\d{1,2})\b/);
    if (embeddedNumeric) {
      const cleanEmb = embeddedNumeric[1].replace(/\./g, '/').replace(/-/g, '/');
      const mEmb = moment(cleanEmb, ['MM/DD/YYYY', 'MM/DD/YY', 'M/D/YYYY', 'M/D/YY', 'YYYY/MM/DD', 'YYYY/M/D']);
      if (mEmb.isValid() && mEmb.year() >= 1900 && mEmb.year() <= 2099) {
        return mEmb.format('YYYY-MM-DD');
      }
    }

    // Formats with month names or standard ISO
    const formats = [
      'MM/DD/YYYY', 'MM/DD/YY', 'M/D/YYYY', 'M/D/YY',
      'MM-DD-YYYY', 'MM-DD-YY', 'M-D-YYYY', 'M-D-YY',
      'YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD',
      'MMMM DD, YYYY', 'MMM DD, YYYY', 'MMMM D, YYYY', 'MMM D, YYYY',
      'MMMM DD YYYY', 'MMM DD YYYY', 'MMMM D YYYY', 'MMM D YYYY',
      'DD MMMM YYYY', 'DD MMM YYYY', 'D MMMM YYYY', 'D MMM YYYY',
      'DD-MMM-YYYY', 'DD-MMM-YY', 'D-MMM-YYYY',
      'YYYY-MM-DDTHH:mm:ss.SSSZ', 'YYYY-MM-DDTHH:mm:ss'
    ];

    const m = moment(str, formats);
    if (m.isValid() && m.year() >= 1900 && m.year() <= 2099) {
      return m.format('YYYY-MM-DD');
    }

    const mNative = moment(new Date(str));
    if (mNative.isValid() && mNative.year() >= 1900 && mNative.year() <= 2099) {
      return mNative.format('YYYY-MM-DD');
    }

    return '';
  }

  /**
   * Helper: Format time string into standard HH:mm format.
   */
  formatTimeString(val: any): string {
    if (!val) return '';
    if (val instanceof Date) {
      return moment(val).format('HH:mm');
    }
    const str = String(val).trim();
    if (/^(?:none|null|undefined|n\/a|na|no|unknown|\.)$/i.test(str)) return '';
    if (/will\s*call|w\/c|cwr/i.test(str)) return '';

    const m = moment(str, [
      'h:mm A', 'hh:mm A', 'h:mm a', 'hh:mm a', 'HH:mm', 'H:mm', 'HH:mm:ss', 'H:mm:ss',
      'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD h:mm A', 'YYYY-MM-DD hh:mm A',
      'M/D/YYYY h:mm A', 'MM/DD/YYYY h:mm A', 'M/D/YYYY hh:mm A', 'MM/DD/YYYY hh:mm A',
      'M/D/YYYY H:mm', 'MM/DD/YYYY HH:mm'
    ]);
    if (m.isValid()) {
      return m.format('HH:mm');
    }

    const timeMatch = str.match(/\b([01]?[0-9]|2[0-3]):([0-5][0-9])(?::([0-5][0-9]))?\s*(AM|PM)?\b/i);
    if (timeMatch) {
      let hrs = parseInt(timeMatch[1], 10);
      const mins = timeMatch[2];
      const ampm = (timeMatch[4] || '').toUpperCase();
      if (ampm === 'PM' && hrs < 12) hrs += 12;
      if (ampm === 'AM' && hrs === 12) hrs = 0;
      return `${String(hrs).padStart(2, '0')}:${mins}`;
    }

    return str;
  }

  /**
   * Match Funding Source from extracted string against fundingSourceList.
   */
  matchFundingSource(extractedText: string, fundingSourceList: any[]): any {
    return matchFundingSource(extractedText, fundingSourceList);
  }

  /**
   * Match Space Type from extracted string against spaceTypeMainList.
   */
  matchSpaceType(extractedText: string, spaceTypeList: any[]): any {
    return matchSpaceType(extractedText, spaceTypeList);
  }
}

export function matchFundingSource(extractedText: string, fundingSourceList: any[]): any {
  if (!extractedText || !fundingSourceList || fundingSourceList.length === 0) return null;
  const clean = extractedText.toLowerCase().trim();

  // 1. Exact match on code, displayName, name, or brokerType
  let match = fundingSourceList.find(fs => {
    const name = (fs.displayName || fs.name || '').toLowerCase();
    const code = (fs.code || fs.companyType || '').toLowerCase();
    return name === clean || code === clean || (fs.aliases && fs.aliases.some((a: string) => a.toLowerCase() === clean));
  });

  if (match) return match;

  // 2. Partial word match
  match = fundingSourceList.find(fs => {
    const name = (fs.displayName || fs.name || '').toLowerCase();
    return name.includes(clean) || clean.includes(name);
  });

  return match || null;
}

export function matchSpaceType(extractedText: string, spaceTypeList: any[]): any {
  if (!extractedText || !spaceTypeList || spaceTypeList.length === 0) return null;
  const clean = extractedText.toLowerCase().trim();

  const isWheelchair = /w\/?c|wheelchair|chair|wc/i.test(clean);
  const isGurney = /gurney|stretcher/i.test(clean);
  const isAmbulatory = /amb|ambulatory|sedan|taxi|walk/i.test(clean);

  let match = spaceTypeList.find(st => {
    const title = (st.title || st.name || '').toLowerCase();
    if (isWheelchair && /wheelchair|w\/?c/i.test(title)) return true;
    if (isGurney && /gurney|stretcher/i.test(title)) return true;
    if (isAmbulatory && /ambulatory|sedan|standard/i.test(title)) return true;
    return title.includes(clean) || clean.includes(title);
  });

  return match || null;
}
